import { makeArticle } from "./learnFactory";

const compareRelated = [
  {
    title: "Compare Treatments",
    href: "/compare-treatments",
    description: "Full comparison of depression treatment options",
  },
  {
    title: "SAINT® vs SPRAVATO®",
    href: "/learn/saint-vs-spravato",
    description: "SAINT vs esketamine comparison",
  },
  {
    title: "SAINT® vs Traditional TMS",
    href: "/learn/saint-vs-traditional-tms",
    description: "SAINT vs TMS comparison",
  },
  {
    title: "TMS vs SPRAVATO®",
    href: "/tms-vs-spravato",
    description: "TMS vs esketamine comparison",
  },
  {
    title: "Schedule a Consultation",
    href: "/contactus",
    description: "Discuss which treatment may fit your situation",
  },
];

export const pageTmsVsMedication = makeArticle({
  slug: "tms-vs-medication",
  title: "TMS vs Antidepressant Medication | Neuro Wellness",
  description:
    "TMS vs antidepressants compared: mechanism, schedule, side effects, FDA status, and research. Learn how brain stimulation differs from medication for depression.",
  canonical: "/learn/tms-vs-medication",
  h1: "TMS vs Antidepressant Medication for Depression",
  breadcrumbLabel: "TMS vs Medication",
  shortAnswer:
    "TMS and antidepressant medication work through different mechanisms. Antidepressants alter chemical neurotransmitter systems throughout the body and are taken daily for months or years. TMS uses magnetic pulses to stimulate a specific brain region, introduces no chemical, and is completed in about six weeks of weekday sessions. TMS is FDA-cleared for adults whose depression hasn't improved on antidepressants. TMS does not cause systemic side effects like weight gain or sexual dysfunction. Neither is universally better — they may be used together.",
  keyTakeaways: [
    "Antidepressants work through chemical systems; TMS uses magnetic brain stimulation.",
    "TMS introduces no chemical into the body; antidepressants are systemic.",
    "TMS is completed in ~6 weeks; antidepressants are taken daily for months or years.",
    "TMS does not cause weight gain, sexual dysfunction, or GI side effects.",
    "TMS is FDA-cleared for depression that hasn't responded to medication.",
  ],
  sections: [
    {
      heading: "Mechanism of Action",
      paragraphs: [
        "Antidepressants work by altering chemical neurotransmitter systems — serotonin, norepinephrine, and dopamine — throughout the brain and body. TMS uses magnetic pulses to stimulate the left dorsolateral prefrontal cortex, a brain region involved in mood regulation. TMS introduces no chemical into the body and targets brain circuitry directly rather than chemical systems.",
      ],
    },
    {
      heading: "Treatment Schedule",
      paragraphs: [
        "Antidepressants are taken daily, typically for months to years, with 6-8 weeks needed to assess effectiveness. TMS is delivered as one session per day, five days a week, for about four to six weeks — roughly 36 sessions. Each session lasts about 19 minutes (or 3 minutes with theta burst). TMS is a time-limited course, not an ongoing daily medication.",
      ],
    },
    {
      heading: "Side Effects",
      paragraphs: [
        "Antidepressant side effects can include weight gain, sexual dysfunction, gastrointestinal issues, drowsiness, insomnia, and emotional blunting. TMS side effects are localized — scalp discomfort, headache, and facial twitching — and usually fade within the first week. TMS does not cause systemic side effects because it introduces no chemical. The most serious TMS risk is seizure, which is rare (under 0.1% of sessions).",
      ],
    },
    {
      heading: "FDA Status",
      paragraphs: [
        "Antidepressants are FDA-approved drugs. TMS devices are FDA-cleared medical devices. The first TMS device was cleared in 2008 for adults with major depressive disorder who haven't improved on antidepressant medication. Both have met FDA standards through different regulatory pathways.",
      ],
    },
    {
      heading: "Insurance Coverage",
      paragraphs: [
        "Antidepressants are broadly covered by insurance. TMS is also covered by most commercial insurance, Medicare, and the VA for treatment-resistant depression when medical-necessity criteria are met. Prior authorization is typically required for TMS, documenting failed medication trials. Coverage for both varies by plan.",
      ],
    },
  ],
  faqs: [
    {
      question: "Is TMS better than antidepressants?",
      answer:
        "Neither is universally better. Antidepressants are the standard first-line treatment. TMS is FDA-cleared for adults whose depression hasn't improved on antidepressants. They work through different mechanisms and may be used together. The appropriate choice depends on your treatment history.",
    },
    {
      question: "Can TMS replace antidepressants?",
      answer:
        "TMS may be an option when antidepressants haven't worked, but it doesn't necessarily replace medication. Many patients continue antidepressants during and after TMS. Whether to continue or adjust medication is a clinical decision. Never stop antidepressants abruptly without supervision.",
    },
    {
      question: "Does TMS have fewer side effects than medication?",
      answer:
        "TMS does not cause systemic side effects like weight gain, sexual dysfunction, or GI issues. TMS side effects are localized (headache, scalp discomfort) and usually fade within a week. However, TMS carries a rare risk of seizure, which medications do not.",
    },
    {
      question: "Is TMS covered by insurance like antidepressants?",
      answer:
        "TMS is covered by most commercial insurance, Medicare, and the VA for treatment-resistant depression when criteria are met, but typically requires prior authorization documenting failed medication trials. Antidepressants are broadly covered. Coverage for both varies by plan.",
    },
    {
      question: "Can I take antidepressants during TMS?",
      answer:
        "Yes. Many patients continue their current antidepressant medication during TMS treatment. TMS does not require you to stop medication. Whether to continue, adjust, or discontinue is a clinical decision made by your psychiatrist.",
    },
  ],
  sources: [
    {
      citation:
        "O'Reardon JP, et al. Efficacy and safety of TMS in the acute treatment of major depression: a multisite randomized controlled trial. Biol Psychiatry. 2007;62(11):1208-1216.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17123640/",
    },
  ],
  relatedLinks: compareRelated,
});
